import { KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useState } from "react";
import { reOrderTemplateSettingMeeting, reOrderTemplateSettingMerchant, reOrderTemplateSettingPipeline, reOrderTemplateSettingReimbursement } from "../api/SettingCRUD";
import useAccessToken from "./AccessToken";
import { usePipelineStore } from "../stores/PipelineStore";
import { useMeetingStore } from "../stores/MeetingsStore";
import { useMerchantStore } from "../stores/MerchantStore";
import { useReimbursementStore } from "../stores/ReimbursementStore";

export interface UseDragAndDropDatabaseProps {
    MouseSensor?: {
        activationConstraint?: {
            distance?: number
        }
    }
    TouchSensor?: {
        activationConstraint?: {
            delay?: number,
            tolerance?: number
        }
    }
}

const useDragAndDropDatabase = (props?: UseDragAndDropDatabaseProps) => {
    const token = useAccessToken()
    const { setMultiPipelines } = usePipelineStore()
    const { setMultiMeetings } = useMeetingStore()
    const { setMultiMerchants } = useMerchantStore()
    const { setMultiReimbursements } = useReimbursementStore()
    const [updatedData, setUpdatedData] = useState<any[]>([]);
    const [type, setType] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleDragEnd = (event: any) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const updateData = (data: any[]) => {
                const oldIndex = data.findIndex(item => item.id === active.id);
                const newIndex = data.findIndex(item => item.id === over.id);
                return arrayMove(data, oldIndex, newIndex);
            }

            const newData = updateData(updatedData);
            if (type === 'pipeline') handleSaveOrderPipeline(newData)
            else if (type === 'meeting') handleSaveOrderMeeting(newData)
            else if (type === 'merchant') handleSaveOrderMerchant(newData)
            else if (type === 'reimbursement') handleSaveOrderReimbursement(newData)
            setUpdatedData(newData);
        }
    }

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: props?.MouseSensor?.activationConstraint?.distance || 10
            }
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: props?.TouchSensor?.activationConstraint?.delay || 250,
                tolerance: props?.TouchSensor?.activationConstraint?.tolerance || 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleSaveOrderPipeline = async (newData: any[]) => {
        const idItems = newData.map((item: any) => item.id);
        setIsLoading(true)

        try {
            const response = await reOrderTemplateSettingPipeline(token, idItems)
            if (response.data.success) {
                setMultiPipelines(newData)
            }
        } catch {
            
        } finally {
            setIsLoading(false)
        }
    }

    const handleSaveOrderMeeting = async (newData: any[]) => {
        const idItems = newData.map((item: any) => item.id);
        setIsLoading(true)

        try {
            const response = await reOrderTemplateSettingMeeting(token, idItems)
            if (response.data.success) {
                setMultiMeetings(newData)
            }
        } catch {
            
        } finally {
            setIsLoading(false)
        }
    }

    const handleSaveOrderMerchant = async (newData: any[]) => {
        const idItems = newData.map((item: any) => item.id);
        setIsLoading(true)

        try {
            const response = await reOrderTemplateSettingMerchant(token, idItems)
            if (response.data.success) {
                setMultiMerchants(newData)
            }
        } catch {
            
        } finally {
            setIsLoading(false)
        }
    }

    const handleSaveOrderReimbursement = async (newData: any[]) => {
        const idItems = newData.map((item: any) => item.id);
        setIsLoading(true)

        try {
            const response = await reOrderTemplateSettingReimbursement(token, idItems)
            if (response.data.success) {
                setMultiReimbursements(newData)
            }
        } catch {
            
        } finally {
            setIsLoading(false)
        }
    }

    return { updatedData, isLoading, setUpdatedData, setType, handleDragEnd, sensors };
}

export default useDragAndDropDatabase;
